import {stringify} from 'query-string';
import {
    fetchUtils,
    GET_LIST,
    GET_ONE,
    GET_MANY,
    GET_MANY_REFERENCE,
    CREATE,
    UPDATE,
    UPDATE_MANY,
    DELETE,
    DELETE_MANY,
} from 'react-admin';

import httpErrorHandler from './http/httpErrorHandler';
import {responseNormalizer} from "./http/normalizer/responseNormalizer";

/**
 * Maps react-admin queries to a json-server powered REST API
 *
 * @see https://github.com/typicode/json-server
 * @example
 * GET_LIST     => GET http://my.api.url/posts?_sort=title&_order=ASC&_start=0&_end=24
 * GET_ONE      => GET http://my.api.url/posts/123
 * GET_MANY     => GET http://my.api.url/posts/123, GET http://my.api.url/posts/456, GET http://my.api.url/posts/789
 * UPDATE       => PUT http://my.api.url/posts/123
 * CREATE       => POST http://my.api.url/posts/123
 * DELETE       => DELETE http://my.api.url/posts/123
 */
export default (apiUrl, httpClient = fetchUtils.fetchJson) => {
    /**
     * @param {String} type One of the constants appearing at the top if this file, e.g. 'UPDATE'
     * @param {String} resource Name of the resource to fetch, e.g. 'posts'
     * @param {Object} params The data request params, depending on the type
     * @returns {Object} { url, options } The HTTP request parameters
     */
    const convertDataRequestToHTTP = (type, resource, params) => {
        let url = '';
        const options = {};

        switch (type) {
            case GET_LIST: {
                const {page, perPage} = params.pagination;
                const {field, order} = params.sort;
                const keyOrder = `order[${field}]`;
                const query = {
                    ...fetchUtils.flattenObject(params.filter),
                    page: page,
                    perPage: perPage,
                    [keyOrder]: order,
                };
                url = `${apiUrl}/${resource}?${stringify(query)}`;
                break;
            }
            case GET_ONE:
                url = `${apiUrl}/${resource}/${params.id}`;
                break;
            case GET_MANY_REFERENCE: {
                const {page, perPage} = params.pagination;
                const {field, order} = params.sort;
                const query = {
                    ...fetchUtils.flattenObject(params.filter),
                    [params.target]: params.id,
                    _sort: field,
                    _order: order,
                    _start: (page - 1) * perPage,
                    _end: page * perPage,
                };
                url = `${apiUrl}/${resource}?${stringify(query)}`;
                break;
            }
            case UPDATE:
                url = `${apiUrl}/${resource}/${params.id}`;
                options.method = 'PUT';
                options.body = JSON.stringify(params.data);
                break;
            case CREATE:
                url = `${apiUrl}/${resource}`;
                options.method = 'POST';
                options.body = JSON.stringify(params.data);
                break;
            case DELETE:
                url = `${apiUrl}/${resource}/${params.id}`;
                options.method = 'DELETE';
                break;
            case GET_MANY: {
                const result = params.ids.map((id) => 'id[]=' + id);

                url = `${apiUrl}/${resource}?${result.join('&')}`;
                break;
            }
            default:
                throw new Error(`Unsupported fetch action type ${type}`);
        }
        return {url, options};
    };

    /**
     * @param {Object} response HTTP response from fetch()
     * @param {String} type One of the constants appearing at the top if this file, e.g. 'UPDATE'
     * @param {String} resource Name of the resource to fetch, e.g. 'posts'
     * @param {Object} params The data request params, depending on the type
     * @returns {Object} Data response
     */
    const convertHTTPResponse = (response, type, resource, params) => {
        response = responseNormalizer(response, type, resource, params);

        if (resource === 'contacts' && type === 'GET_ONE') {
            if (response.json.attributes.hasOwnProperty('hydra:member')) {
                let attributes = {};

                response.json.attributes['hydra:member'].map((attribute) => {
                    attributes[attribute.attributeId] = {
                        value: attribute.value
                    }

                    return attribute;
                });

                response.json.attributes = attributes;
            }
        }

        if (resource === 'contacts' && type === 'GET_LIST') {
            response.json['hydra:member'].map((c) => {
                let attributes = {};

                c.attributes.map((a) => {
                    attributes[a.attributeId] = {
                        value: a.value
                    }

                    return a;
                });

                c.attributes = attributes;

                return c;
            });
        }

        const {json} = response;
        let value = {};
        switch (type) {
            case GET_LIST:
            case GET_MANY_REFERENCE:
                if (!json.hasOwnProperty('hydra:totalItems')) {
                    throw new Error(
                        'The X-Total-Count header is missing in the HTTP Response. The jsonServer Data Provider expects responses for lists of resources to contain this header with the total number of results to build the pagination. If you are using CORS, did you declare X-Total-Count in the Access-Control-Expose-Headers header?'
                    );
                }

                value = {
                    data: json['hydra:member'],
                    total: parseInt(json['hydra:totalItems'], 10),
                };
                return value;
            case GET_MANY:
                value = {
                    data: json['hydra:member']
                };
                return value;
            case CREATE:
                return {data: {...params.data, id: json.id}};
            case DELETE:
                return {
                    data: {
                        id: null
                    }
                };
            default:
                return {data: json};
        }
    };

    /**
     * Convert a `File` object returned by the upload input into a base 64 string.
     * That's not the most optimized way to store images in production, but it's
     * enough to illustrate the idea of data provider decoration.
     */
    const convertFileToBase64 = file => new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(file.rawFile);

        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
    });

    /**
     * @param {string} type Request type, e.g GET_LIST
     * @param {string} resource Resource name, e.g. "posts"
     * @param {Object} payload Request parameters. Depends on the request type
     * @returns {Promise} the Promise for a data response
     */
    return (type, resource, params) => {
        // json-server doesn't handle filters on UPDATE route, so we fallback to calling UPDATE n times instead
        if (type === UPDATE_MANY) {
            return Promise.all(
                params.ids.map(id =>
                    httpClient(`${apiUrl}/${resource}/${id}`, {
                        method: 'PUT',
                        body: JSON.stringify(params.data),
                    })
                )
            ).then(responses => ({
                data: responses.map(response => response.json),
            }));
        }
        // json-server doesn't handle filters on DELETE route, so we fallback to calling DELETE n times instead
        if (type === DELETE_MANY) {
            return Promise.all(
                params.ids.map(id =>
                    httpClient(`${apiUrl}/${resource}/${id}`, {
                        method: 'DELETE',
                    })
                )
            ).then(responses => ({
                data: responses.map(response => response.json),
            }));
        }

        if (type === 'CREATE' && resource === 'blacklist/contacts/import') {
            if (params.data.file) {
                return convertFileToBase64(params.data.file)
                    .then(base64File => {
                        let data = JSON.stringify({
                            data: base64File,
                            filename: params.data.file.title
                        });
                        let options = {
                            method: 'POST',
                            body: data
                        };

                        return httpClient(`${apiUrl}/blacklist/contacts/import-file`, options).then(response =>
                            convertHTTPResponse(response, type, resource, params)
                        ).catch(({body, status}) => {
                            return httpErrorHandler(status, body);
                        });
                    });
            }
        }

        // @todo do we still use this?
        if (type === 'CREATE' && resource === 'contacts/import') {
            if (params.data.file) {
                return convertFileToBase64(params.data.file)
                    .then(base64File => {
                        let data = JSON.stringify({
                            data: base64File,
                            filename: params.data.file.title
                        });
                        let options = {
                            method: 'POST',
                            body: data
                        };

                        return httpClient(`${apiUrl}/contacts/import-file`, options).then(response =>
                            convertHTTPResponse(response, type, resource, params)
                        ).catch(({body, status}) => {
                            return httpErrorHandler(status, body);
                        });
                    });
            }
        }

        /* if (type === 'GET_ONE' && resource === 'contacts') {
             console.log('GET_ONE');

             return httpClient(url, options)
                 .then(response => {
                     console.log('hierzo');
                     console.log(response);
                     convertHTTPResponse(response, type, resource, params)
                 })
                 .catch(({body, status}) => {
                     return httpErrorHandler(status, body);
                 });
         }*/

        const {url, options} = convertDataRequestToHTTP(
            type,
            resource,
            params
        );

        return httpClient(url, options).then(response =>
            convertHTTPResponse(response, type, resource, params)
        ).catch(({body, status}) => {
            return httpErrorHandler(status, body);
        });
    };
};
